import type {
    ErrorTypePayload,
    ErrorTypeResponse,
} from '@/types/apps/error-type.type';
import type {
    ApiResponse,
    PaginatedResult,
    QueryParams,
} from '@/types/common/api.type';
import type { BaseApiClientInterface } from '@/types/service/base-api-client.interface';
import type { Creatable } from '@/types/service/creatable.interface';
import type { Deletable } from '@/types/service/deletable.interface';
import type { Indexable } from '@/types/service/indexable.interface';
import type { Showable } from '@/types/service/showable.interface';
import type { Updatable } from '@/types/service/updatable.interface';

type ErrorTypeListResponse = ApiResponse<PaginatedResult<ErrorTypeResponse>>;

export class ErrorTypeService
    implements Showable, Updatable, Deletable, Creatable, Indexable
{
    public constructor(private apiClient: BaseApiClientInterface) {}

    protected endpoints = {
        base: () => '/api/error-types',
        detail: (id: string) => `/api/error-types/${id}`,
        import: () => '/api/error-types/import',
        export: () => '/api/error-types/export',
    };

    public index(params?: QueryParams): Promise<ErrorTypeListResponse> {
        return this.apiClient.fetchApi(this.endpoints.base(), params);
    }

    public detail(
        id: string,
        params?: QueryParams
    ): Promise<ApiResponse<ErrorTypeResponse>> {
        const url = this.endpoints.detail(id);

        return this.apiClient.fetchApi(url, params);
    }

    public create(
        payload: ErrorTypePayload
    ): Promise<ApiResponse<ErrorTypeResponse>> {
        return this.apiClient.postApi(this.endpoints.base(), payload);
    }

    public update(
        id: string,
        payload: ErrorTypePayload
    ): Promise<ApiResponse<ErrorTypeResponse>> {
        return this.apiClient.putApi(this.endpoints.detail(id), payload);
    }
    public destroy(
        ids: string[],
        isHardDeleted: boolean = false
    ): Promise<ApiResponse<void>> {
        const url = this.endpoints.base();

        return this.apiClient.destroyApi<void>(url, ids, isHardDeleted);
    }

    public async importFile(file: File): Promise<any> {
        const form = this.apiClient.buildFormData({ file });

        return this.apiClient.postApi<any>(this.endpoints.import(), form);
    }

    public async exportFile(): Promise<Blob> {
        return this.apiClient.fetchBlob(this.endpoints.export());
    }
}
